// Listing Builder — taslak listesi (durum sekmeleri, filtreler, toplu islemler).
// Editor: pages/EtsyListingBuilderPage.tsx (ListingForm)

import { authHeaders } from "../../lib/http";
import { useShopVertical } from "../../lib/useShopVertical";
import { useActiveShopId } from "../../store/shop.store";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShotProducts } from "../../lib/useShotProducts";
import { useJewelryTypes } from "../../lib/useJewelryTypes";
import { productJewelryType } from "../../lib/jewelryType";
import {
  ShoppingBag,
  Image as ImageIcon,
  Plus,
  Trash2,
  Tag,
  X,
  Loader2,
  FileText,
  Search,
  Calendar,
  Settings2,
  Upload,
  RefreshCw,
  ExternalLink,
  Rocket,
  AlertTriangle,
  DollarSign,
  Truck,
  Copy,
  CheckCircle2,
  Video,
  ChevronLeft,
  ChevronRight,
  LayoutTemplate,
  Layers,
  Heart,
  Eye,
  ShoppingCart,
  ArrowUpDown,
} from "lucide-react";

const PAGE_SIZE = 20;
const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
import { useShippingProfiles, shippingProfileColor } from "../../lib/shippingProfiles";
import { seasonPassed, seasonLeadDays } from "@erc/shared";
import { PriceRecalcModal } from "./PriceRecalcModal";
import { ReworkModal } from "./ReworkModal";

import {
  API_BASE, getStudioExports, effectiveStatus, isBoundHere, calcListingScoreLite, scoreColor, statusConfig,
  type DraftListItem, type DuplicateGroup,
} from "../../lib/listingDrafts";

const getAuthHeaders = authHeaders;

// ======== Dashboard - Listing Table ========

export function DraftListView({ drafts, loading, onRefresh }: { drafts: DraftListItem[]; loading: boolean; onRefresh: () => void }) {
  const navigate = useNavigate();
  const activeShop = useActiveShopId();
  // Bos liste nedenini soyleyebilmek icin aktif magazanin adi/dikeyi
  const { shop: activeShopRec, vertical: shopVertical } = useShopVertical();
  const shopName = activeShopRec?.label || activeShopRec?.shopName || null;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState(false);
  // Etsy aktivasyon / senkron
  const [syncing, setSyncing] = useState(false);
  // Etsy videolarini tamamla: yayindaki ilanlarda Etsy'de video yoksa uret + yukle (arka planda)
  const [videoFix, setVideoFix] = useState<{ running: boolean; total: number; checked: number; hadVideo: number; uploaded: number; generated: number; errors: string[] } | null>(null);
  useEffect(() => {
    if (!videoFix?.running) return;
    const t = setInterval(async () => {
      const r = await fetch(`${API_BASE}/api/etsy/listings/ensure-videos`, { headers: getAuthHeaders() }).catch(() => null);
      const j = r?.ok ? await r.json().catch(() => null) : null;
      if (j?.data) { setVideoFix(j.data); if (!j.data.running) onRefresh(); }
    }, 4000);
    return () => clearInterval(t);
  }, [videoFix?.running]); // eslint-disable-line react-hooks/exhaustive-deps
  async function handleEnsureVideos() {
    if (!confirm("Yayındaki tüm ilanlar Etsy'de kontrol edilecek; videosu olmayanlara video üretilip yüklenecek (arka planda, birkaç dakika). Devam edilsin mi?")) return;
    const r = await fetch(`${API_BASE}/api/etsy/listings/ensure-videos`, { method: "POST", headers: getAuthHeaders() });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setActionMsg(j.error || "Başlatılamadı"); return; }
    setVideoFix(j.data);
  }
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [resyncingId, setResyncingId] = useState<string | null>(null);
  const [videoSyncId, setVideoSyncId] = useState<string | null>(null);
  const [bulkResyncing, setBulkResyncing] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [customOnly, setCustomOnly] = useState(false);
  // Kategori (taki: urun turu; tekstil: sablon kategorisi), siralama, gorselsiz
  const [categoryFilter, setCategoryFilter] = useState("");
  // Kargo profili filtresi ("" tumu, "__none" profilsiz)
  const [profileFilter, setProfileFilter] = useState("");
  const [sortBy, setSortBy] = useState<"updated" | "created" | "title" | "price-asc" | "price-desc" | "images" | "sales" | "favorites" | "views">("updated");
  // Performans suzgecleri: satis gelen / favorisi olan listeler
  const [soldOnly, setSoldOnly] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  // IP temizligi (2026-10-26): yakin kopya (Yakin/Benzer) ve sezonu gecmis listeler; satis/favori YOK
  const [nearOnly, setNearOnly] = useState(false);
  const [pastSeasonOnly, setPastSeasonOnly] = useState(false);
  const [coldOnly, setColdOnly] = useState(false);
  const pastSeason = useCallback((d: DraftListItem) => seasonPassed(`${d.title} ${d.tags.join(" ")}`), []);
  // Yeniden hazirlanacak: IP temizliginde pasife alinanlar. Tasarim Dengeli'ye degisince (kopya degil)
  // Etsy'de geri acilabilir; sira: yenilenenler, sezonu yakin olan, cok goruntulenen
  const [reworkOnly, setReworkOnly] = useState(false);
  // Toplu yeniden hazirlama: secilenler icin uretim baslatma (ids) ya da yalniz onay ekrani ([])
  const [reworkModal, setReworkModal] = useState<string[] | null>(null);
  // Etsy'den silinenler (etsyListingId yok) burada degil
  const ipParked = (d: DraftListItem) => d.status === "archived" && !!d.etsyListingId && /IP temizliği/.test(d.publishNote ?? "");
  // Acilabilir yalniz tasarim gercekten degistiyse ya da kademesi kopya degilse; kademesi bilinmeyen
  // (Custom Studio vb.) kopya sayilir
  const stillCopy = (d: DraftListItem) => !/Tasarım değişti/.test(d.publishNote ?? "") && !["balanced", "loose", "original"].includes(d.fidelity ?? "");
  const leadDays = useCallback((d: DraftListItem) => seasonLeadDays(`${d.title} ${d.tags.join(" ")}`), []);
  // Toplu Etsy'de pasife al / geri ac (arka planda)
  const [bulkActive, setBulkActive] = useState<{ running: boolean; active: boolean; total: number; done: number; ok: number; errors: string[] } | null>(null);
  useEffect(() => {
    if (!bulkActive?.running) return;
    const t = setInterval(async () => {
      const r = await fetch(`${API_BASE}/api/etsy/listings/set-active-bulk`, { headers: getAuthHeaders() }).catch(() => null);
      const j = r?.ok ? await r.json().catch(() => null) : null;
      if (j?.data) { setBulkActive(j.data); if (!j.data.running) onRefresh(); }
    }, 3000);
    return () => clearInterval(t);
  }, [bulkActive?.running]); // eslint-disable-line react-hooks/exhaustive-deps
  async function handleBulkSetActive(active: boolean) {
    let sel = drafts.filter((d) => selectedIds.has(d.id) && d.etsyListingId);
    // IP temizliginde kapatilan liste tasarimi degismeden (hala kopya) geri acilmaz
    const blocked = active ? sel.filter((d) => ipParked(d) && stillCopy(d)) : [];
    if (blocked.length) {
      sel = sel.filter((d) => !blocked.includes(d));
      setActionMsg(`${blocked.length} liste hâlâ ilhamın kopyası, açılmadı: önce "Tasarımı değiştir" ile Dengeli tasarıma geçirin.`);
    }
    if (!sel.length) { if (!blocked.length) setActionMsg("Seçili listelerde Etsy'de olan yok"); return; }
    if (!confirm(active
      ? `${sel.length} liste Etsy'de yeniden AÇILACAK (görünür olur). Devam edilsin mi?`
      : `${sel.length} liste Etsy'de PASİFE alınacak: aramada ve dükkânda görünmez, şikâyet edilemez. Silinmez; "Etsy'de geri aç" ile açılabilir. Devam edilsin mi?`)) return;
    setBulkAction(false);
    const r = await fetch(`${API_BASE}/api/etsy/listings/set-active-bulk`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ ids: sel.map((d) => d.id), active }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setActionMsg(j.error || "Başlatılamadı"); return; }
    setBulkActive(j.data);
    setSelectedIds(new Set());
  }
  const [page, setPage] = useState(1);
  const [noImagesOnly, setNoImagesOnly] = useState(false);
  const isJewelry = shopVertical === "jewelry";
  // Satirdaki kargo profili etiketi: kimlik -> ad (aktif magazanin profilleri)
  const shippingProfiles = useShippingProfiles(activeShop);
  const shippingTitle = (id: string) => shippingProfiles.find((p) => p.id === id)?.title ?? `#${id}`;
  // Toplu cogaltma: secilen her kargo profili icin bir kopya (secim yoksa ayni profille tek kopya)
  const [showBulkDup, setShowBulkDup] = useState(false);
  const [dupProfiles, setDupProfiles] = useState<string[]>([]);
  const [dupBusy, setDupBusy] = useState<string | null>(null);
  const { data: shotData } = useShotProducts(isJewelry);
  const jewelryTypes = useJewelryTypes();
  const categoryOf = useCallback((d: DraftListItem): string => {
    if (d.supplierCategoryId) {
      const cat = shotData?.catalogs.find((c) => c.id === d.supplierCatalogId)
        ?? shotData?.catalogs.find((c) => (c.categories as Array<{ id: string }>).some((p) => p.id === d.supplierCategoryId));
      const prod = (cat?.categories as Array<{ id: string; name?: string; productTypeKey?: string }> | undefined)?.find((p) => p.id === d.supplierCategoryId);
      const k = productJewelryType(prod);
      if (k) return jewelryTypes.find((t) => t.key === k)?.label ?? k;
    }
    if (d.category) return d.category.split(/[>/]/).pop()!.trim();
    return "";
  }, [shotData, jewelryTypes]);
  const categoryOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of drafts) { const c = categoryOf(d); m.set(c, (m.get(c) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [drafts, categoryOf]);
  // Toplu planlama
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  // Taki: urun maliyeti/kargosu degisince fiyatlari toplu guncelleme
  const [showPriceRecalc, setShowPriceRecalc] = useState(false);
  const [bulkStart, setBulkStart] = useState("");
  const [bulkIntervalMin, setBulkIntervalMin] = useState(60);
  const [bulkScheduling, setBulkScheduling] = useState(false);
  // Gorsel uzerine gelince buyuk onizleme (fixed konum, tablonun overflow'undan bagimsiz)
  const [hoverImg, setHoverImg] = useState<{ url: string; top: number; left: number } | null>(null);
  function showHoverPreview(url: string, el: HTMLElement) {
    const r = el.getBoundingClientRect();
    const PREVIEW = 300;
    const spaceRight = window.innerWidth - r.right;
    const left = spaceRight > PREVIEW + 20 ? r.right + 10 : r.left - PREVIEW - 10;
    let top = r.top + r.height / 2 - PREVIEW / 2;
    top = Math.max(12, Math.min(top, window.innerHeight - PREVIEW - 12));
    setHoverImg({ url, top, left });
  }

  useEffect(() => {
    function processStudioExports() {
      const data = getStudioExports();
      if (data.images.length === 0) return;
      // Aktarim burada TUKETILMEZ: editor tasarim, sablon ve SEO bilgisiyle
      // birlikte okur (once yalnizca gorseller tasiniyordu)
      if (data.draftId) {
        navigate(`/listing-builder?id=${data.draftId}`, { replace: true });
      } else {
        navigate("/listing-builder?new=true", { replace: true });
      }
    }
    processStudioExports();
    function handleStudioReady() { processStudioExports(); }
    window.addEventListener("studio-exports-ready", handleStudioReady);
    return () => window.removeEventListener("studio-exports-ready", handleStudioReady);
  }, [navigate]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // Ust kutu yalniz bu sayfadaki listeleri secer/birakir
  const toggleAll = () => {
    const allOn = pageItems.length > 0 && pageItems.every((d) => selectedIds.has(d.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageItems.forEach((d) => (allOn ? next.delete(d.id) : next.add(d.id)));
      return next;
    });
  };

  async function handleDeleteSingle(id: string) {
    setDeleting(true);
    try {
      await fetch(`${API_BASE}/api/listing-drafts/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      onRefresh();
    } catch {}
    setDeleting(false);
    setDeleteConfirmId(null);
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await fetch(`${API_BASE}/api/listing-drafts/bulk-delete`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      setSelectedIds(new Set());
      onRefresh();
    } catch {}
    setDeleting(false);
    setBulkAction(false);
  }

  // Mevcut Etsy listesindeki gorselleri silip draft'taki TAM listeyi yeniden gonder
  async function handleResyncImages(id: string) {
    if (!confirm("Etsy'deki mevcut görseller silinip taslaktaki tüm görseller (mockup + size chart) yeniden yüklenecek. Devam?")) return;
    setResyncingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/resync-images`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: id }),
      });
      const json = await res.json();
      if (res.ok) { setActionMsg(`Görseller yeniden gönderildi — ${json.data.summary}`); onRefresh(); }
      else setActionMsg(json.error || "Görsel senkronu başarısız");
    } catch { setActionMsg("Görsel senkronu başarısız (bağlantı hatası)"); }
    setResyncingId(null);
  }

  // Yalniz video: taslagin guncel fotograflarindan yeni video uretip Etsy'dekini degistirir
  async function handleResyncVideo(id: string) {
    if (!confirm("Taslağın güncel fotoğraflarından yeni video üretilecek ve Etsy'deki video bununla değiştirilecek. Görsellere dokunulmaz. Devam?")) return;
    setVideoSyncId(id);
    setActionMsg("Video üretiliyor ve Etsy'ye gönderiliyor…");
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/resync-video`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: id, regenerate: true }),
      });
      const json = await res.json();
      if (res.ok) { setActionMsg(`Video güncellendi — ${json.data.summary}`); onRefresh(); }
      else setActionMsg(json.error || "Video senkronu başarısız");
    } catch { setActionMsg("Video senkronu başarısız (bağlantı hatası)"); }
    setVideoSyncId(null);
  }

  // Secili listelerin gorsellerini toplu yeniden gonder (sirayla)
  async function handleBulkResync() {
    const ids = filtered.filter((d) => selectedIds.has(d.id) && d.etsyListingId).map((d) => d.id);
    if (ids.length === 0) { setActionMsg("Etsy'e gönderilmiş liste seçili değil."); setBulkAction(false); return; }
    if (!confirm(`${ids.length} liste için Etsy görselleri silinip taslaktaki tüm görseller yeniden yüklenecek. Devam?`)) return;
    setBulkResyncing(true); setBulkAction(false);
    let ok = 0;
    for (let i = 0; i < ids.length; i++) {
      setActionMsg(`Görseller yenileniyor… (${i + 1}/${ids.length})`);
      try {
        const res = await fetch(`${API_BASE}/api/etsy/listings/resync-images`, {
          method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: ids[i] }),
        });
        if (res.ok) ok++;
      } catch { /* devam */ }
    }
    setActionMsg(`${ok}/${ids.length} listenin görselleri yeniden gönderildi.`);
    setSelectedIds(new Set()); setBulkResyncing(false); onRefresh();
  }

  // Mevcut Etsy taslagini DOGRUDAN active'e cek (kopya olusturmaz)
  async function handleActivate(id: string) {
    setActivatingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/activate`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: id }),
      });
      const json = await res.json();
      if (res.ok) { setActionMsg("Etsy'de yayına alındı ✓"); onRefresh(); }
      else setActionMsg(json.error || "Aktive edilemedi");
    } catch { setActionMsg("Aktive edilemedi (bağlantı hatası)"); }
    setActivatingId(null);
  }

  // Secili taslaklari toplu active'e cek
  async function handleBulkActivate() {
    const ids = filtered.filter((d) => selectedIds.has(d.id) && d.etsyListingId && d.status !== "published").map((d) => d.id);
    if (ids.length === 0) { setActionMsg("Aktive edilecek uygun taslak yok (Etsy'e gönderilmiş & yayında olmayan)."); setBulkAction(false); return; }
    setBulkActivating(true); setBulkAction(false);
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/activate-many`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftIds: ids }),
      });
      const json = await res.json();
      if (res.ok) { setActionMsg(`${json.data.activated}/${ids.length} liste Etsy'de yayına alındı`); setSelectedIds(new Set()); onRefresh(); }
      else setActionMsg(json.error || "Toplu aktive başarısız");
    } catch { setActionMsg("Toplu aktive başarısız (bağlantı hatası)"); }
    setBulkActivating(false);
  }

  // Hazir (engeli olmayan) ve Etsy'ye hic gonderilmemis secili taslaklari
  // yayin kuyruguna al — sayfa kapansa da sunucuda tamamlanir
  const [bulkPublishing, setBulkPublishing] = useState(false);
  async function handleBulkPublish(state: "draft" | "active") {
    const sel = filtered.filter((d) => selectedIds.has(d.id));
    const ids = sel.filter((d) => d.status === "ready" && !d.etsyListingId).map((d) => d.id);
    const skipped = sel.length - ids.length;
    setBulkAction(false);
    if (ids.length === 0) { setActionMsg("Gönderilecek uygun liste yok — yalnızca 'Etsy'ye hazır' olup Etsy'ye henüz gönderilmemiş listeler kuyruğa alınır."); return; }
    if (state === "active" && !confirm(`${ids.length} liste Etsy'de YAYINA alınacak (ücretlendirilir). Devam edilsin mi?`)) return;
    setBulkPublishing(true);
    let ok = 0;
    for (const id of ids) {
      const r = await fetch(`${API_BASE}/api/etsy/listings/publish-queue`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: id, state }),
      }).catch(() => null);
      if (r?.ok) ok++;
    }
    setBulkPublishing(false);
    setSelectedIds(new Set());
    setActionMsg(`${ok}/${ids.length} liste Etsy kuyruğuna alındı (${state === "active" ? "yayına" : "Etsy taslağı olarak"})${skipped ? ` · ${skipped} liste hazır olmadığı ya da zaten Etsy'de olduğu için atlandı` : ""}. Durum birkaç dakika içinde güncellenir.`);
    onRefresh();
  }

  // Etsy'de ayni urunden birden fazla aktif liste (senkron sonucu)
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);

  // Etsy'deki GERCEK active/draft durumunu panele yansit
  async function handleSyncEtsyState() {
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/sync-state`, { method: "POST", headers: getAuthHeaders() });
      const json = await res.json();
      if (res.ok) {
        const d = json.data;
        setActionMsg(`Senkronlandı — Etsy'de ${d.activeOnEtsy} aktif · ${d.toPublished} yayına, ${d.toDraft} taslağa çekildi (${d.checked} kontrol edildi)`);
        // Ayni urunden Etsy'de birden fazla aktif liste: hangisinin dogru
        // oldugunu panel bilemez, silmeyi kullanici yapar — burada gosterilir.
        setDuplicates((d.duplicates ?? []) as DuplicateGroup[]);
        onRefresh();
      } else setActionMsg(json.error || "Senkronizasyon hatası");
    } catch { setActionMsg("Senkronizasyon hatası (bağlantı)"); }
    setSyncing(false);
  }

  // Sayfa acilinca Etsy durumu, son senkron 30 dakikadan eskiyse kendiliginden cekilir
  // (sunucudaki 30 dk'lik tur Etsy kotasi icin 6 saate cekildi, 22/10)
  useEffect(() => {
    if (!activeShop) return;
    const k = `erc:etsy-sync-at:${activeShop}`;
    try {
      if (Date.now() - Number(localStorage.getItem(k) || 0) < 30 * 60_000) return;
      localStorage.setItem(k, String(Date.now()));
    } catch { return; }
    void handleSyncEtsyState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShop]);

  async function handleBulkStatus(status: string) {
    if (selectedIds.size === 0) return;
    try {
      await fetch(`${API_BASE}/api/listing-drafts/bulk-status`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ids: Array.from(selectedIds), status }),
      });
      setSelectedIds(new Set());
      onRefresh();
    } catch {}
    setBulkAction(false);
  }

  /** Secili listeleri cogalt. Baslik aynen kalir; kopya "Taslak/Hazir" olarak
   *  bu magazada acilir, Etsy'ye ayrica yuklenir. */
  async function handleBulkDuplicate() {
    const ids = filtered.filter((d) => selectedIds.has(d.id)).map((d) => d.id);
    if (ids.length === 0) return;
    if (!dupProfiles.length) return;
    const profiles = dupProfiles;
    const total = ids.length * profiles.length;
    let done = 0, failed = 0, taken = 0;
    setDupBusy(`0/${total}`);
    try {
      for (const id of ids) {
        const own = drafts.find((d) => d.id === id)?.shippingProfileId;
        for (const pid of profiles) {
          // Liste zaten bu profilde: ayni profille kopya acilmaz
          if (pid === own) { taken++; setDupBusy(`${done + failed + taken}/${total}`); continue; }
          const r = await fetch(`${API_BASE}/api/listing-drafts/${id}/duplicate`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify({ shippingProfileId: pid }),
          }).catch(() => null);
          if (r?.ok) done++; else if (r?.status === 409) taken++; else failed++;
          setDupBusy(`${done + failed + taken}/${total}`);
        }
      }
      setSelectedIds(new Set());
      setShowBulkDup(false);
      setActionMsg(`${done} kopya oluşturuldu.${taken ? ` ${taken} tanesi atlandı — liste o kargo profiliyle zaten var.` : ""}${failed ? ` ${failed} tanesi oluşturulamadı, tekrar deneyin.` : ""}`);
      onRefresh();
    } finally { setDupBusy(null); }
  }

  // Toplu planlama modalini ac: baslangic = mevcut son plandan sonrasi (yoksa bir sonraki tam saat)
  function openBulkSchedule() {
    setBulkAction(false);
    const now = new Date();
    // Mevcut (gelecekteki) planlanan listelerin en gec zamani — yeni plan onun ustune binmesin
    const lastScheduled = drafts
      .filter((d) => d.status === "scheduled" && d.scheduledAt)
      .map((d) => new Date(d.scheduledAt as string).getTime())
      .filter((t) => !isNaN(t) && t > now.getTime())
      .sort((a, b) => b - a)[0];
    const base = new Date(Math.max(now.getTime(), lastScheduled ?? 0));
    // Son plan varsa +interval, yoksa bir sonraki tam saat
    if (lastScheduled) base.setMinutes(base.getMinutes() + bulkIntervalMin);
    else { base.setMinutes(0, 0, 0); base.setHours(base.getHours() + 1); }
    const local = new Date(base.getTime() - base.getTimezoneOffset() * 60000);
    setBulkStart(local.toISOString().slice(0, 16));
    setShowBulkSchedule(true);
  }

  // Secili listeleri, baslangictan itibaren esit araliklarla planla
  async function handleBulkSchedule() {
    const ids = filtered.filter((d) => selectedIds.has(d.id)).map((d) => d.id);
    const start = new Date(bulkStart);
    if (ids.length === 0 || isNaN(start.getTime())) return;
    setBulkScheduling(true);
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i++) {
        const when = new Date(start.getTime() + i * bulkIntervalMin * 60000);
        const r = await fetch(`${API_BASE}/api/listing-drafts/${ids[i]}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: "scheduled", scheduledAt: when.toISOString() }),
        }).catch(() => null);
        if (!r || !r.ok) failed++;
      }
      setSelectedIds(new Set());
      setShowBulkSchedule(false);
      setActionMsg(failed
        ? `${ids.length - failed}/${ids.length} liste planlandı — ${failed} tanesi planlanamadı, tekrar deneyin.`
        : `${ids.length} liste planlandı.`);
      onRefresh();
    } finally {
      setBulkScheduling(false);
    }
  }

  const filtered = useMemo(() => {
    let items = drafts;
    if (statusFilter !== "all") items = items.filter((d) => effectiveStatus(d, activeShop) === statusFilter);
    if (customOnly) items = items.filter((d) => d.tags.some((t) => t.toLowerCase() === "custom"));
    if (categoryFilter) items = items.filter((d) => categoryOf(d) === (categoryFilter === "__none" ? "" : categoryFilter));
    if (profileFilter) items = items.filter((d) => (profileFilter === "__none" ? !d.shippingProfileId : d.shippingProfileId === profileFilter));
    if (noImagesOnly) items = items.filter((d) => d.imageCount === 0);
    if (soldOnly) items = items.filter((d) => (d.sales ?? 0) > 0);
    if (nearOnly) items = items.filter((d) => d.fidelity === "close" || d.fidelity === "near");
    if (pastSeasonOnly) items = items.filter((d) => pastSeason(d).passed);
    if (coldOnly) items = items.filter((d) => (d.sales ?? 0) === 0 && (d.favorites ?? 0) === 0);
    if (favOnly) items = items.filter((d) => (d.favorites ?? 0) > 0);
    if (reworkOnly) {
      items = items.filter(ipParked);
      if (sortBy === "updated") items = [...items].sort((a, b) =>
        Number(stillCopy(a)) - Number(stillCopy(b)) || leadDays(a) - leadDays(b) || (b.views ?? 0) - (a.views ?? 0));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      // SKU (REX-1857) ve Etsy ilan no da aranir
      items = items.filter((d) => d.title.toLowerCase().includes(q) || d.tags.some((t) => t.toLowerCase().includes(q))
        || (d.sku ?? "").toLowerCase().includes(q) || String(d.etsyListingId ?? "").includes(q));
    }
    if (sortBy !== "updated") {
      items = [...items].sort((a, b) =>
        sortBy === "created" ? (Date.parse(b.createdAt ?? "") || 0) - (Date.parse(a.createdAt ?? "") || 0)
        : sortBy === "title" ? a.title.localeCompare(b.title, "tr")
        : sortBy === "price-asc" ? (a.price ?? 0) - (b.price ?? 0)
        : sortBy === "price-desc" ? (b.price ?? 0) - (a.price ?? 0)
        // Esitlikte ikincil olcut: satista favori, favoride goruntulenme
        : sortBy === "sales" ? (b.sales ?? 0) - (a.sales ?? 0) || (b.favorites ?? 0) - (a.favorites ?? 0)
        : sortBy === "favorites" ? (b.favorites ?? 0) - (a.favorites ?? 0) || (b.views ?? 0) - (a.views ?? 0)
        : sortBy === "views" ? (b.views ?? 0) - (a.views ?? 0)
        : b.imageCount - a.imageCount);
    }
    return items;
  }, [drafts, statusFilter, searchQuery, customOnly, activeShop, categoryFilter, profileFilter, noImagesOnly, soldOnly, favOnly, nearOnly, pastSeasonOnly, coldOnly, reworkOnly, sortBy, categoryOf, pastSeason, leadDays]);

  // Sayfalama: suzgec degisince ilk sayfaya don
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [statusFilter, searchQuery, customOnly, activeShop, categoryFilter, profileFilter, noImagesOnly, soldOnly, favOnly, nearOnly, pastSeasonOnly, coldOnly, reworkOnly, sortBy]);
  // Ozet: suzulen listelerin toplam satis/favori/goruntulenmesi
  const totals = useMemo(() => filtered.reduce((t, d) => ({
    sales: t.sales + (d.sales ?? 0), favorites: t.favorites + (d.favorites ?? 0), views: t.views + (d.views ?? 0),
    sold: t.sold + ((d.sales ?? 0) > 0 ? 1 : 0),
  }), { sales: 0, favorites: 0, views: 0, sold: 0 }), [filtered]);
  const curPage = Math.min(page, pageCount);
  const pageItems = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: drafts.length };
    for (const d of drafts) {
      const s = effectiveStatus(d, activeShop);
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [drafts, activeShop]);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ShoppingBag className="h-5 w-5 text-indigo-600" /> Listing Builder
          </h1>
          <p className="mt-1 text-sm text-gray-500">Etsy ürün listelerinizi oluşturun ve yönetin</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleSyncEtsyState} disabled={syncing}
            title="Etsy'deki gerçek yayında/taslak durumunu, satış, favori ve görüntülenme sayılarını çekip paneli güncelle"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Etsy'yi senkronla
          </button>
          <button onClick={() => void handleEnsureVideos()} disabled={!!videoFix?.running}
            title="Yayındaki ilanların videosunu Etsy'den kontrol et; olmayanlara video üretip yükle"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {videoFix?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
            {videoFix?.running ? `Videolar ${videoFix.checked}/${videoFix.total}` : "Videoları tamamla"}
          </button>
          {isJewelry ? (
            <>
              <Link to="/supplier-catalogs" className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <ImageIcon className="h-4 w-4" /> Katalog
              </Link>
              <Link to="/listing-review/jewelry" className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                <Plus className="h-4 w-4" /> Liste Denetimi
              </Link>
            </>
          ) : (
            <>
              <Link to="/mockup-studio" className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <ImageIcon className="h-4 w-4" /> Mockup Studio
              </Link>
              <Link to="/listing-builder?new=true" className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                <Plus className="h-4 w-4" /> Listing Oluştur
              </Link>
            </>
          )}
        </div>
      </div>
      {videoFix && !videoFix.running && (
        <p className={`-mt-4 mb-4 text-right text-xs ${videoFix.errors.length ? "text-amber-700" : "text-emerald-700"}`} title={videoFix.errors.join("\n")}>
          {videoFix.total} ilan: {videoFix.hadVideo} zaten videolu, {videoFix.uploaded} yüklendi ({videoFix.generated} yeni üretildi){videoFix.errors.length ? `, ${videoFix.errors.length} hata` : ""}
        </p>
      )}

      {isJewelry && (shotData?.items.some((i) => i.drafts.length === 0) ?? false) && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-xs text-fuchsia-800">
          <span>{shotData!.items.filter((i) => i.drafts.length === 0).length} ürünün fotoğrafı hazır ama taslağı yok.</span>
          <Link to="/jewelry-products" className="font-medium underline">Ürünler'de gör</Link>
        </div>
      )}

      {actionMsg && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
          <span>{actionMsg}</span>
          <button onClick={() => setActionMsg(null)} className="text-indigo-400 hover:text-indigo-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">
                Etsy'de {duplicates.length} ürünün birden fazla aktif listesi var
              </p>
              <p className="mt-0.5 text-[11px] text-amber-700">
                Aynı SKU/başlık ile açılmış kopyalar. Hangisinin kalacağını siz seçin — fazlasını
                Etsy'de silin veya pasife alın. "Panelde yok" işaretli olan genelde fazlalık olandır.
              </p>
            </div>
            <button onClick={() => setDuplicates([])} className="text-amber-500 hover:text-amber-700">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="mt-2 space-y-1.5">
            {duplicates.map((g) => (
              <li key={g.key} className="rounded border border-amber-200 bg-white/60 px-2 py-1.5">
                <p className="truncate font-medium text-amber-900">{g.listings[0]?.title || g.key}</p>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {g.listings.map((l) => (
                    <a key={l.id} href={l.url || `https://www.etsy.com/listing/${l.id}`}
                      target="_blank" rel="noreferrer"
                      className="text-[11px] text-indigo-600 hover:underline">
                      #{l.id}{l.sku ? ` · ${l.sku}` : ""} {l.linked ? "(panelde bağlı)" : "(panelde yok)"}
                    </a>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filtre karti: ust satir durum sekmeleri + arama, alt satir suzgecler + siralama */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="-mx-1 flex min-w-0 max-w-full gap-1 overflow-x-auto px-1" role="tablist" aria-label="Durum">
            {[
              { key: "all", label: "Tümü" }, { key: "draft", label: "Taslak" }, { key: "ready", label: "Hazır" },
              { key: "scheduled", label: "Planlı" }, { key: "published", label: "Yayında" }, { key: "archived", label: "Arşiv" },
            ].map((tab) => (
              <button key={tab.key} onClick={() => setStatusFilter(tab.key)} role="tab" aria-selected={statusFilter === tab.key}
                className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${statusFilter === tab.key ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50"}`}>
                {tab.label}
                {(statusCounts[tab.key] || 0) > 0 && (
                  <span className={`rounded-full px-1.5 text-xs ${statusFilter === tab.key ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"}`}>{statusCounts[tab.key]}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative ml-auto min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Başlık, etiket, SKU ya da Etsy no…"
              aria-label="Listelerde ara"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          {([
            [soldOnly, setSoldOnly, "Satış gelen", ShoppingCart, "Etsy'de en az bir satışı olan listeler"],
            [favOnly, setFavOnly, "Favorisi olan", Heart, "En az bir kişinin favorilediği listeler"],
            [noImagesOnly, setNoImagesOnly, "Görselsiz", ImageIcon, "Sadece görseli olmayan taslaklar"],
            [nearOnly, setNearOnly, "Yakın kopya", AlertTriangle, "Tasarımı ilhamın Yakın/Benzer kopyası olan listeler (telif şikâyeti riski)"],
            [pastSeasonOnly, setPastSeasonOnly, "Sezonu geçmiş", Calendar, "Sezonu bitmiş ya da kargo süresi yüzünden artık yetişmeyen listeler"],
            [coldOnly, setColdOnly, "Satış/favori yok", X, "Hiç satışı ve favorisi olmayan listeler"],
            [reworkOnly, setReworkOnly, "Yeniden hazırlanacak", RefreshCw, "IP temizliğinde pasife alınanlar: tasarımı yenilenenler önce, sonra sezonu yaklaşan ve çok görüntülenenler. Tasarım Dengeli'ye geçince \"Etsy'de geri aç\" ile açılır"],
            [customOnly, setCustomOnly, "Custom", Tag, "Sadece kişiselleştirilebilir (custom) listeler"],
          ] as const).map(([on, set, label, Icon, title]) => (
            <button key={label} onClick={() => set((v) => !v)} aria-pressed={on} title={title}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
          {categoryOptions.length > 1 && (
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} title="Kategori" aria-label="Kategori"
              className="max-w-[200px] rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              <option value="">Tüm kategoriler</option>
              {categoryOptions.map(([c, n]) => <option key={c || "__none"} value={c || "__none"}>{c || "Kategorisiz"} ({n})</option>)}
            </select>
          )}
          {shippingProfiles.length > 0 && (
            <select value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)} title="Kargo profili" aria-label="Kargo profili"
              className="max-w-[200px] rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              <option value="">Tüm kargo profilleri</option>
              {shippingProfiles.map((pf) => <option key={pf.id} value={pf.id}>{pf.title} ({drafts.filter((d) => d.shippingProfileId === pf.id).length})</option>)}
              <option value="__none">Profilsiz ({drafts.filter((d) => !d.shippingProfileId).length})</option>
            </select>
          )}
          {isJewelry && (
            <button onClick={() => setShowPriceRecalc(true)}
              title="Ürünlerin güncel maliyet + kargosuyla fiyatları yeniden hesapla; değişiklikleri önce göster"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">
              <DollarSign className="h-3.5 w-3.5" /> Fiyatları güncelle
            </button>
          )}
          <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500">
            <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Sıralama</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} title="Sıralama"
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
              <option value="updated">Son güncellenen</option>
              <option value="created">Son oluşturulan</option>
              <option value="sales">En çok satan</option>
              <option value="favorites">En çok favori alan</option>
              <option value="views">En çok görüntülenen</option>
              <option value="price-desc">Fiyat (azalan)</option>
              <option value="price-asc">Fiyat (artan)</option>
              <option value="title">Başlık (A-Z)</option>
              <option value="images">Görsel sayısı</option>
            </select>
          </label>
        </div>
      </div>

      {/* Ozet: suzulen listelerin performansi */}
      <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-1 px-1 text-xs text-gray-500">
        <span><b className="font-semibold text-gray-900">{filtered.length}</b> liste</span>
        {filtered.length > 0 && selectedIds.size === 0 && (
          <button onClick={() => setSelectedIds(new Set(filtered.map((d) => d.id)))} className="font-medium text-indigo-600 hover:underline">Tümünü seç</button>
        )}
        <span className="inline-flex items-center gap-1"><ShoppingCart className="h-3.5 w-3.5 text-emerald-600" /><b className="font-semibold text-gray-900">{totals.sales}</b> satış · {totals.sold} listede</span>
        <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-rose-500" /><b className="font-semibold text-gray-900">{totals.favorites}</b> favori</span>
        <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5 text-gray-400" /><b className="font-semibold text-gray-900">{totals.views.toLocaleString("tr-TR")}</b> görüntülenme</span>
        {reworkOnly && (
          <button onClick={() => setReworkModal([])} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
            <CheckCircle2 className="h-3.5 w-3.5" /> Onay ekranı
          </button>
        )}
      </div>

      {reworkModal && (
        <ReworkModal startIds={reworkModal.length ? reworkModal : undefined} onClose={() => setReworkModal(null)} onChanged={onRefresh} />
      )}
      {bulkActive && (
        <div className={`mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${bulkActive.running ? "bg-indigo-50 text-indigo-700" : bulkActive.errors.length ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}
          title={bulkActive.errors.join("\n")}>
          {bulkActive.running && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {bulkActive.active ? "Etsy'de geri açılıyor" : "Etsy'de pasife alınıyor"}: {bulkActive.done}/{bulkActive.total} · {bulkActive.ok} başarılı{bulkActive.errors.length ? ` · ${bulkActive.errors.length} hata (üzerine gel)` : ""}
          {!bulkActive.running && <button onClick={() => setBulkActive(null)} aria-label="Kapat" className="ml-auto"><X className="h-3.5 w-3.5" /></button>}
        </div>
      )}

      {/* Toplu islem cubugu: secim varken */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2">
          <span className="text-sm font-medium text-indigo-700">{selectedIds.size} liste seçili</span>
          {selectedIds.size < filtered.length && (
            <button onClick={() => setSelectedIds(new Set(filtered.map((d) => d.id)))}
              className="rounded-lg border border-indigo-300 bg-white px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100">Tümünü seç ({filtered.length})</button>
          )}
          <button onClick={() => setSelectedIds(new Set())} className="text-xs text-indigo-600 hover:underline">Seçimi temizle</button>
          <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <button onClick={() => setBulkAction(!bulkAction)} aria-expanded={bulkAction}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
                  <Settings2 className="h-3.5 w-3.5" /> Toplu işlem
                </button>
                {bulkAction && (
                  <div className="absolute right-0 top-full z-20 mt-1 min-w-[220px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    <button onClick={openBulkSchedule} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Toplu Planla</button>
                    <button onClick={() => { setBulkAction(false); setShowBulkDup(true); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-1.5"><Copy className="h-3 w-3" /> Toplu Çoğalt</button>
                    <button onClick={handleBulkActivate} disabled={bulkActivating} className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 text-emerald-700 font-medium flex items-center gap-1.5">
                      {bulkActivating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />} Etsy'de Aktive Et
                    </button>
                    <button onClick={handleBulkResync} disabled={bulkResyncing} className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 text-indigo-700 font-medium flex items-center gap-1.5">
                      {bulkResyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />} Görselleri Yenile
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button onClick={() => void handleBulkPublish("draft")} disabled={bulkPublishing} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-1.5">
                      {bulkPublishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Etsy'ye taslak olarak yükle
                    </button>
                    <button onClick={() => void handleBulkPublish("active")} disabled={bulkPublishing} className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 text-emerald-700 font-medium flex items-center gap-1.5">
                      {bulkPublishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />} Etsy'de yayınla
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button onClick={() => void handleBulkSetActive(false)} disabled={!!bulkActive?.running} className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 text-red-600 font-medium flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Etsy'de pasife al</button>
                    <button onClick={() => void handleBulkSetActive(true)} disabled={!!bulkActive?.running} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-1.5"><Rocket className="h-3 w-3" /> Etsy'de geri aç</button>
                    <button onClick={() => { setBulkAction(false); setReworkModal([...selectedIds]); }}
                      title="Seçilen listeler için ilham analizinden Dengeli tasarım üret; onay ekranında kontrol edip Etsy'de geri aç"
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 text-indigo-700 font-medium flex items-center gap-1.5"><RefreshCw className="h-3 w-3" /> Yeni tasarım üret (toplu)</button>
                    <hr className="my-1 border-gray-100" />
                    <button onClick={() => handleBulkStatus("archived")} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50">Arşivle</button>
                    <button onClick={() => handleBulkStatus("draft")} title="Arşivden çıkar — durum kontrole göre Taslak/Hazır olur" className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50">Arşivden çıkar</button>
                    <hr className="my-1 border-gray-100" />
                    <button onClick={handleBulkDelete} disabled={deleting}
                      className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1.5">
                      {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Sil ({selectedIds.size})
                    </button>
                  </div>
                )}
              </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
          <FileText className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-base font-semibold text-gray-600 mb-1">{drafts.length === 0 ? "Henüz listing yok" : "Sonuç bulunamadı"}</h3>
          {/* Liste AKTIF MAGAZAYA ve dikeyine gore suzuluyor. Taki magazasi
              seciliyken tekstil taslaklari gorunmez; "listelerim kayboldu"
              yerine nedenini soyle. */}
          {drafts.length === 0 && shopVertical !== "apparel" ? (
            <>
              <p className="text-sm text-gray-500 mb-1">
                Aktif mağaza: <span className="font-medium text-gray-700">{shopName || "?"}</span> ({shopVertical})
              </p>
              {isJewelry ? (
                <>
                  <p className="text-sm text-gray-400 mb-4">
                    Bu mağazada henüz taslak yok. Ürünler sayfasındaki fotoğrafı üretilmiş ürünlerden liste oluşturun
                    ya da Liste Denetimi&apos;nde toplu taslağa çevirin.
                  </p>
                  <Link to="/listing-review/jewelry" className="flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-fuchsia-700">
                    Liste Denetimi
                  </Link>
                </>
              ) : (
                <p className="text-sm text-gray-400 mb-4">
                  Bu ekran tekstil/hediyelik listeleri içindir — tekstil listelerini görmek için üst menüden tekstil mağazasına geçin.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-4">Yeni bir listing taslağı oluşturun veya Mockup Studio&apos;dan aktarın</p>
              <Link to="/listing-builder?new=true" className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
                <Plus className="h-4 w-4" /> Listing Oluştur
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[960px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 w-8">
                  <input type="checkbox" checked={pageItems.length > 0 && pageItems.every((d) => selectedIds.has(d.id))}
                    onChange={toggleAll} aria-label="Bu sayfadakileri seç" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                </th>
                <th className="w-24 px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Görsel</th>
                <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Başlık</th>
                <th className="w-32 px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">Durum</th>
                <th className="w-28 px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500" title="Etsy'deki satış, favori ve görüntülenme">Performans</th>
                <th className="w-20 px-3 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500">Fiyat</th>
                <th className="w-36 px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tarih</th>
                <th className="w-16 px-2 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">Puan</th>
                <th className="w-10 px-2 py-3"><span className="sr-only">İşlem</span></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((draft) => {
                const score = calcListingScoreLite(draft);
                const thumbUrl = draft.thumbUrl;
                return (
                  <tr key={draft.id} className="group border-b border-gray-100 align-top transition-colors hover:bg-gray-50">
                    <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(draft.id)} aria-label="Listeyi seç"
                        onChange={() => toggleSelect(draft.id)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                    </td>
                    <td className="px-3 py-3">
                      {/* Gercek baglanti: orta tik / Ctrl+tik listeyi yeni sekmede acar */}
                      <Link
                        to={`/listing-builder?id=${draft.id}`}
                        className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100 transition hover:ring-2 hover:ring-indigo-300"
                        onMouseEnter={thumbUrl ? (e) => showHoverPreview(thumbUrl.startsWith("http") ? thumbUrl : `${API_BASE}${thumbUrl}`, e.currentTarget) : undefined}
                        onMouseLeave={() => setHoverImg(null)}
                      >
                        {thumbUrl ? <img src={thumbUrl.startsWith("http") ? thumbUrl : `${API_BASE}${thumbUrl}`} alt="" loading="lazy" className="w-full h-full object-cover" /> : <ShoppingBag className="h-7 w-7 text-gray-300" />}
                      </Link>
                    </td>
                    <td className="min-w-0 px-3 py-3">
                      <Link to={`/listing-builder?id=${draft.id}`} title={draft.title} className="line-clamp-2 break-words text-sm font-medium text-gray-900 transition-colors hover:text-indigo-600">{draft.title || "Başlıksız Listing"}</Link>
                      {/* Ayrinti: sablon, mockup grubu, big print, SKU, video */}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1" title="Listing şablonu">
                          <LayoutTemplate className="h-3 w-3 text-gray-400" /> {draft.templateName ?? <span className="text-gray-400">Şablon yok</span>}
                        </span>
                        {(draft.mockupTemplates?.length ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1" title="Mockup grubu">
                            <Layers className="h-3 w-3 text-gray-400" /> <span className="max-w-[220px] truncate">{draft.mockupTemplates!.join(", ")}</span>
                          </span>
                        )}
                        {categoryOf(draft) && <span title="Kategori">{categoryOf(draft)}</span>}
                        {draft.sku && <span title="SKU" className="font-mono">{draft.sku}</span>}
                        <span className="inline-flex items-center gap-1" title="Görsel sayısı"><ImageIcon className="h-3 w-3 text-gray-400" /> {draft.imageCount || 0}</span>
                        <span className="inline-flex items-center gap-1" title="Etiket sayısı"><Tag className="h-3 w-3 text-gray-400" /> {draft.tags.length}</span>
                        {draft.bigPrint && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700" title="Big print liste (+$5, 13×17,5 inç baskı)">Big print</span>}
                        {draft.videoUrl && <span className="inline-flex items-center gap-1" title="Videosu var"><Video className="h-3 w-3 text-gray-400" /> Video</span>}
                        {draft.variationCount > 0 && <span title="Varyasyon ekseni">{draft.variationCount} varyasyon</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {/* Kargo profili: ayni liste farkli profillerle cogaltildiginda ayirt etmek icin renkli */}
                        {draft.shippingProfileId ? (
                          <span title={`Kargo profili: ${shippingTitle(draft.shippingProfileId)}`}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${shippingProfileColor(draft.shippingProfileId)}`}>
                            <Truck className="h-3 w-3" /> {shippingTitle(draft.shippingProfileId)}
                          </span>
                        ) : (
                          <span title="Kargo profili yok — Etsy yayını reddeder"
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
                            <Truck className="h-3 w-3" /> Kargo yok
                          </span>
                        )}
                        {draft.tags.some((t) => t.toLowerCase() === "custom") && (
                          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Custom</span>
                        )}
                        {draft.tags.slice(0, 3).map((t, i) => <span key={i} className="max-w-[140px] truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{t}</span>)}
                        {draft.tags.length > 3 && <span className="text-xs text-gray-500">+{draft.tags.length - 3}</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1">
                        {/* Etsy islemleri SADECE kayit secili magazaya bagliysa gosterilir;
                            aksi halde bu butonlar baska magazanin listesine islem yapmaya
                            calisir ve Etsy hata doner. */}
                        {isBoundHere(draft, activeShop) && (
                          <>
                            <a href={`https://www.etsy.com/listing/${draft.etsyListingId}`} target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                              <ExternalLink className="h-3.5 w-3.5" /> Etsy'de aç
                            </a>
                            {draft.status !== "published" && (
                              <button onClick={(e) => { e.stopPropagation(); handleActivate(draft.id); }} disabled={activatingId === draft.id}
                                title="Etsy'deki bu taslağı doğrudan yayına al (kopya oluşturmaz)"
                                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                                {activatingId === draft.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />} Aktive et
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); handleResyncImages(draft.id); }} disabled={resyncingId === draft.id}
                              title="Etsy'deki görselleri silip taslaktaki tüm görselleri (mockup + size chart) yeniden gönder"
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50">
                              {resyncingId === draft.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Görselleri yenile
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleResyncVideo(draft.id); }} disabled={videoSyncId === draft.id}
                              title="Güncel fotoğraflardan yeni video üretip yalnız Etsy'deki videoyu değiştir"
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50">
                              {videoSyncId === draft.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Video className="h-3.5 w-3.5" />} Videoyu yenile
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-center">
                      {(() => {
                        const eff = effectiveStatus(draft, activeShop);
                        return (
                          <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig[eff]?.color || "bg-gray-100 text-gray-600"}`}>
                            {statusConfig[eff]?.label || eff}
                          </span>
                        );
                      })()}
                      {(draft.status === "draft" || draft.status === "ready") && (draft.readiness?.blockers.length ?? 0) > 0 && (
                        <div className="mt-1 line-clamp-2 text-xs text-red-600" title={draft.readiness!.blockers.join("\n")}>
                          {draft.readiness!.blockers.length} eksik · {draft.readiness!.blockers[0]}
                        </div>
                      )}
                      {draft.status === "scheduled" && draft.scheduledAt && (
                        <div className="mt-1 flex items-center justify-center gap-1 text-xs text-indigo-700">
                          <Calendar className="h-3 w-3" />
                          {new Date(draft.scheduledAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                      {/* Planli VE Etsy'de zaten var: liste taslak olarak gonderilmis,
                          saati gelince aktive edilecek. Onceki yayin notu burada
                          uyari gibi duruyordu ("Yayinlandi!" + unlem) — durum
                          acikca yazilir. */}
                      {ipParked(draft) && (
                        <div className={`mx-auto mt-1 max-w-[180px] text-xs font-medium leading-tight ${stillCopy(draft) ? "text-amber-700" : "text-emerald-700"}`}>
                          {stillCopy(draft) ? "Kopya: tasarımı değiştir" : "Tasarım yenilendi: açılabilir"}
                          {leadDays(draft) > 0 && <span className="block font-normal text-gray-500">Sezon {leadDays(draft)} gün sonra</span>}
                        </div>
                      )}
                      {draft.status === "scheduled" && draft.etsyListingId ? (
                        <div className="mx-auto mt-1 max-w-[180px] text-xs leading-tight text-gray-500"
                          title={draft.publishNote ?? undefined}>
                          Etsy&apos;de taslak olarak hazır — planlanan saatte yayına alınacak
                        </div>
                      ) : draft.publishNote && draft.status !== "published" ? (
                        <div className="mx-auto mt-1 max-w-[180px] text-xs leading-tight text-amber-700" title={draft.publishNote}>
                          <AlertTriangle className="mr-0.5 inline h-3 w-3" />{draft.publishNote.length > 60 ? draft.publishNote.slice(0, 60) + "…" : draft.publishNote}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      {draft.etsyListingId ? (
                        <div className="space-y-1 text-xs">
                          <div className={`flex items-center gap-1.5 ${(draft.sales ?? 0) > 0 ? "font-semibold text-emerald-700" : "text-gray-500"}`} title="Satılan adet">
                            <ShoppingCart className="h-3.5 w-3.5" /> {draft.sales ?? 0} satış
                          </div>
                          <div className={`flex items-center gap-1.5 ${(draft.favorites ?? 0) > 0 ? "text-rose-600" : "text-gray-500"}`} title="Favori">
                            <Heart className="h-3.5 w-3.5" /> {draft.favorites ?? 0}
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-500" title="Görüntülenme">
                            <Eye className="h-3.5 w-3.5" /> {(draft.views ?? 0).toLocaleString("tr-TR")}
                          </div>
                        </div>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-4 text-right text-sm font-semibold text-gray-900">{draft.price ? `$${draft.price}` : "-"}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-left text-xs text-gray-500">
                      <div title="Oluşturulma">{fmtDateTime(draft.createdAt)}</div>
                      {draft.updatedAt !== draft.createdAt && <div className="mt-0.5 text-[10px] text-gray-500" title="Son güncelleme">güncellendi {fmtDateTime(draft.updatedAt)}</div>}
                    </td>
                    <td className="px-2 py-4 text-center"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${scoreColor(score)}`}>{score}</span></td>
                    <td className="px-2 py-4 text-center">
                      <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(draft.id); }}
                        className="rounded-lg p-1.5 text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 focus:opacity-100 group-hover:opacity-100"
                        title="Sil" aria-label="Listeyi sil">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            <span>{(curPage - 1) * PAGE_SIZE + 1}–{Math.min(curPage * PAGE_SIZE, filtered.length)} / {filtered.length} listing</span>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(curPage - 1)} disabled={curPage === 1} aria-label="Önceki sayfa" title="Önceki sayfa"
                  className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-200 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                {Array.from({ length: pageCount }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === pageCount || Math.abs(n - curPage) <= 2)
                  .map((n, i, arr) => (
                    <span key={n} className="flex items-center gap-1">
                      {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-gray-400">…</span>}
                      <button onClick={() => setPage(n)}
                        className={`min-w-[28px] rounded-lg px-2 py-1 font-medium ${n === curPage ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-200"}`}>{n}</button>
                    </span>
                  ))}
                <button onClick={() => setPage(curPage + 1)} disabled={curPage === pageCount} aria-label="Sonraki sayfa" title="Sonraki sayfa"
                  className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-200 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showPriceRecalc && (
        <PriceRecalcModal
          visibleIds={drafts.map((d) => d.id)}
          activeShopId={activeShop}
          onClose={() => setShowPriceRecalc(false)}
          onDone={(msg) => { setShowPriceRecalc(false); setActionMsg(msg); onRefresh(); }}
        />
      )}

      {showBulkDup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !dupBusy && setShowBulkDup(false)}>
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Copy className="h-4 w-4 text-sky-600" /> Toplu Çoğalt ({selectedIds.size} liste)</h3>
              <button onClick={() => !dupBusy && setShowBulkDup(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
            </div>
            <p className="mb-2 text-xs text-gray-600">Kargo profili seç — her seçili profil için listenin bir kopyası açılır. Başlık aynen kalır.</p>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {shippingProfiles.length === 0 ? <span className="text-xs text-gray-400">Kargo profilleri alınamadı</span> : shippingProfiles.map((pf) => {
                const on = dupProfiles.includes(pf.id);
                return (
                  <button key={pf.id} onClick={() => setDupProfiles((cur) => (on ? cur.filter((x) => x !== pf.id) : [...cur, pf.id]))}
                    className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${on ? `${shippingProfileColor(pf.id)} font-semibold ring-1 ring-current` : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
                    {on && <CheckCircle2 className="h-3 w-3" />}<Truck className="h-3 w-3" /> {pf.title}
                  </button>
                );
              })}
            </div>
            <p className="mb-4 text-[11px] text-gray-500">
              {dupProfiles.length
                ? `${selectedIds.size} × ${dupProfiles.length} = ${selectedIds.size * dupProfiles.length} kopya oluşturulacak.`
                : "En az bir kargo profili seçin. Liste zaten o profildeyse ya da SKU o profilde listelenmişse kopya atlanır."}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowBulkDup(false)} disabled={!!dupBusy} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50">Vazgeç</button>
              <button onClick={() => void handleBulkDuplicate()} disabled={!!dupBusy || !dupProfiles.length}
                className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50">
                {dupBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {dupBusy}</> : <><Copy className="h-3.5 w-3.5" /> Çoğalt</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkSchedule && (() => {
        const ids = filtered.filter((d) => selectedIds.has(d.id));
        const start = new Date(bulkStart);
        const valid = !isNaN(start.getTime());
        // Zaten planlanmis (gelecekteki) listeler — kullanicinin uzerine plan yapmamasi icin goster
        const selectedSet = new Set(ids.map((d) => d.id));
        const existingScheduled = drafts
          .filter((d) => d.status === "scheduled" && d.scheduledAt && !selectedSet.has(d.id))
          .map((d) => ({ ...d, ts: new Date(d.scheduledAt as string).getTime() }))
          .filter((d) => !isNaN(d.ts))
          .sort((a, b) => a.ts - b.ts);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowBulkSchedule(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2"><Calendar className="h-5 w-5 text-purple-600" /> Toplu Planla</h3>
              <p className="text-sm text-gray-500 mb-4"><b>{ids.length}</b> liste seçili başlangıçtan itibaren eşit aralıklarla planlanacak.</p>

              {existingScheduled.length > 0 && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-600 block mb-1">Mevcut planlananlar ({existingScheduled.length})</label>
                  <div className="max-h-28 overflow-y-auto rounded-lg border border-purple-100 bg-purple-50/40 divide-y divide-purple-50">
                    {existingScheduled.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className="w-28 flex-shrink-0 font-medium text-purple-600">
                          {new Date(d.ts).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="flex-1 truncate text-gray-500" title={d.title}>{d.title || "Başlıksız"}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-[10px] text-gray-400">Başlangıç, son plandan sonrasına ayarlandı — üzerine binmemesi için kontrol edin.</p>
                </div>
              )}

              <label className="text-xs font-medium text-gray-600 block mb-1">Başlangıç tarihi/saati</label>
              <input type="datetime-local" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 mb-3 focus:ring-2 focus:ring-purple-200 focus:outline-none" />

              <label className="text-xs font-medium text-gray-600 block mb-1">Aralık</label>
              <div className="flex gap-2 mb-4">
                {[30, 60, 120, 240, 1440].map((m) => (
                  <button key={m} onClick={() => setBulkIntervalMin(m)}
                    className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${bulkIntervalMin === m ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {m === 1440 ? "1 gün" : m >= 60 ? `${m / 60} saat` : `${m} dk`}
                  </button>
                ))}
              </div>

              {valid && ids.length > 0 && (
                <>
                <label className="text-xs font-medium text-gray-600 block mb-1">Yeni planlanacaklar ({ids.length})</label>
                <div className="mb-4 max-h-40 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
                  {ids.map((d, i) => (
                    <div key={d.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                      <span className="w-28 flex-shrink-0 font-medium text-purple-700">
                        {new Date(start.getTime() + i * bulkIntervalMin * 60000).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="flex-1 truncate text-gray-600" title={d.title}>{d.title || "Başlıksız"}</span>
                    </div>
                  ))}
                </div>
                </>
              )}

              <div className="flex gap-2">
                <button onClick={handleBulkSchedule} disabled={!valid || ids.length === 0 || bulkScheduling}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50">
                  {bulkScheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />} {bulkScheduling ? "Planlanıyor…" : "Planla"}
                </button>
                <button onClick={() => setShowBulkSchedule(false)} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
              </div>
            </div>
          </div>
        );
      })()}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirmId(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full m-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Listing Sil?</h3>
            <p className="text-sm text-gray-600 mb-5">Bu taslak kalıcı olarak silinecek. Bu işlem geri alınamaz.</p>
            <div className="flex gap-2">
              <button onClick={() => handleDeleteSingle(deleteConfirmId)} disabled={deleting}
                className="flex items-center gap-1.5 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-40">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Sil
              </button>
              <button onClick={() => setDeleteConfirmId(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* Gorsel hover onizlemesi (buyuk) */}
      {hoverImg && (
        <div className="fixed z-[60] pointer-events-none" style={{ top: hoverImg.top, left: hoverImg.left }}>
          <img src={hoverImg.url} alt="" className="w-[300px] h-[300px] object-contain rounded-xl border border-gray-200 bg-white shadow-2xl" />
        </div>
      )}
    </div>
  );
}

// ======== Listing Form (Template-editor style) ========

